import { Reveal } from "@/components/Reveal";

const STAGES = [
  {
    n: "01",
    name: "DISCOVER",
    tagline: "Understand before you invest.",
    items: ["Stakeholder interviews", "Current-state workflows", "Root causes & user needs", "System constraints"],
  },
  {
    n: "02",
    name: "DEFINE",
    tagline: "Specify the change precisely.",
    items: ["Business & functional requirements", "Epics, stories, acceptance criteria", "Future-state workflows", "Priorities"],
  },
  {
    n: "03",
    name: "VALIDATE",
    tagline: "Make it tangible early.",
    items: ["Prototypes & proofs-of-concept", "Process simulations", "Solution options", "Vendor evaluation"],
  },
  {
    n: "04",
    name: "DELIVER",
    tagline: "Keep build tied to intent.",
    items: ["Product ownership", "Backlog leadership", "Implementation oversight", "Vendor coordination"],
  },
  {
    n: "05",
    name: "MEASURE",
    tagline: "Prove it worked.",
    items: ["Adoption & operating outcomes", "Error reduction", "Cycle time", "Quality & cost"],
  },
];

export default function ServiceModelSection() {
  return (
    <section data-testid="service-model-section" className="border-b border-line">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
        <Reveal>
          <p className="mb-6 flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-slate-500">
            <span className="h-px w-10 bg-slate-300" aria-hidden="true" />
            03 — THE SERVICE MODEL
          </p>
        </Reveal>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal delay={0.08}>
            <h2 className="max-w-2xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Discover. Define. Validate. Deliver. Measure.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="max-w-md leading-relaxed text-slate-600">
              A clean line from the first question to a proven outcome — so modernization
              efforts solve the right problems, not just the visible ones.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-5">
          {STAGES.map((stage, i) => (
            <Reveal key={stage.n} delay={i * 0.08} className="h-full">
              <article
                data-testid={`stage-card-${stage.name.toLowerCase()}`}
                className="group flex h-full flex-col bg-white p-6 transition-colors duration-300 hover:bg-canvas"
              >
                <p className="font-serif text-5xl leading-none text-slate-200 transition-colors duration-300 group-hover:text-accent/30">
                  {stage.n}
                </p>
                <h3 className="mt-6 font-mono text-xs tracking-[0.25em] text-ink">
                  {stage.name}
                </h3>
                <p className="mt-2 font-serif text-lg italic leading-snug text-slate-700">
                  {stage.tagline}
                </p>
                <ul className="mt-6 border-t border-line">
                  {stage.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-2.5 border-b border-line py-2 text-xs leading-snug text-slate-600"
                    >
                      <span className="block h-1 w-1 shrink-0 bg-slate-300" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 grid gap-8 border border-line bg-white p-8 sm:p-12 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-slate-400">
                THE DIFFERENTIATOR
              </p>
              <p className="mt-4 font-serif text-3xl leading-snug text-ink sm:text-4xl">
                “Is this what you mean?”
              </p>
            </div>
            <p className="max-w-xl leading-relaxed text-slate-600">
              We don't only write requirements documents. We quickly turn ambiguous ideas
              into prototypes stakeholders can see and react to — dramatically improving
              discovery and requirements quality before an expensive implementation team
              starts building.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
